/*
  LFDL - global.js (rebuild)
  ------------------------------------------------------------
  Le global.js précédent était cassé (erreur de syntaxe), ce qui
  bloquait en cascade : panier (drawer), suppression, loading infini,
  et plein de comportements Dawn.

  Ce rebuild fournit un socle stable "Dawn-like" avec les briques
  nécessaires au thème LFDL :
  - focus trap (trapFocus / removeTrapFocus)
  - pub/sub (subscribe / publish + PUB_SUB_EVENTS)
  - fetchConfig()
  - QuantityInput (plus / moins)
  - VariantSelects (sélection variante → input id + URL)
  - ProductRecommendations (lazy load)

  On privilégie la robustesse : si quelque chose manque, on ne casse pas
  toute la page.
*/

/* ---------------------------
   Utils
--------------------------- */

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

function debounce(fn, wait) {
  let t;
  return function debounced(...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/* ---------------------------
   Focus trap (cart drawer, modals)
--------------------------- */

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  if (!container) return;

  const elements = getFocusableElements(container);
  const first = elements[0];
  const last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;
    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = () => {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = (event) => {
    if (String(event.code || "").toUpperCase() !== "TAB") return;
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first && first.focus();
    }
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last && last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  try {
    elementToFocus && elementToFocus.focus();
    if (
      elementToFocus &&
      elementToFocus.tagName === "INPUT" &&
      ["search", "text", "email", "url"].includes(elementToFocus.type) &&
      elementToFocus.value
    ) {
      elementToFocus.setSelectionRange(0, elementToFocus.value.length);
    }
  } catch {
    // noop
  }
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);
  if (elementToFocus) {
    try {
      elementToFocus.focus();
    } catch {
      // noop
    }
  }
}

function onKeyUpEscape(event) {
  if (String(event.code || "").toUpperCase() !== "ESCAPE") return;
  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;
  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  if (summaryElement) {
    summaryElement.setAttribute("aria-expanded", "false");
    summaryElement.focus();
  }
}

// Expose (cart-drawer.js appelle trapFocus/removeTrapFocus)
window.trapFocus = trapFocus;
window.removeTrapFocus = removeTrapFocus;
window.onKeyUpEscape = onKeyUpEscape;

/* ---------------------------
   Details accessibility
--------------------------- */

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute("aria-expanded", summary.parentNode.hasAttribute("open") ? "true" : "false");
  if (summary.nextElementSibling && summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }
  summary.addEventListener("click", (event) => {
    const details = event.currentTarget.closest("details");
    event.currentTarget.setAttribute("aria-expanded", details && !details.hasAttribute("open") ? "true" : "false");
  });
  if (summary.closest("header-drawer, menu-drawer")) return;
  summary.parentElement && summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

/* ---------------------------
   Menu drawer (minimal controller for mobile facets)
--------------------------- */

class MenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.onRootToggle = this.onRootToggle.bind(this);
    this.onDelegatedClick = this.onDelegatedClick.bind(this);
  }

  connectedCallback() {
    this.bindEvents();
    requestAnimationFrame(() => this.bindEvents());
  }

  bindEvents() {
    this.rootDetails = this.querySelector(":scope > details") || this.querySelector("details");
    if (!this.rootDetails) return;

    this.rootSummary = this.rootDetails.querySelector(":scope > summary");

    if (this.rootDetails.dataset.menuDrawerRootBound !== "1") {
      this.rootDetails.dataset.menuDrawerRootBound = "1";
      this.rootDetails.addEventListener("toggle", this.onRootToggle);
      this.rootDetails.addEventListener("keyup", onKeyUpEscape);
    }

    if (this.dataset.menuDrawerDelegatedBound !== "1") {
      this.dataset.menuDrawerDelegatedBound = "1";
      this.addEventListener("click", this.onDelegatedClick);
    }

    if (this.rootSummary && this.rootSummary.dataset.menuDrawerSummaryBound !== "1") {
      this.rootSummary.dataset.menuDrawerSummaryBound = "1";
      this.rootSummary.setAttribute("role", "button");
      this.rootSummary.addEventListener("click", () => {
        requestAnimationFrame(() => this.syncRootState());
      });
    }

    const rootClose = this.rootDetails.querySelector(":scope > summary .mobile-facets__close");
    if (rootClose && rootClose.dataset.menuDrawerCloseBound !== "1") {
      rootClose.dataset.menuDrawerCloseBound = "1";
      rootClose.addEventListener("click", (event) => {
        event.preventDefault();
        this.close();
      });
    }

    this.querySelectorAll("details.mobile-facets__details").forEach((details) => {
      if (details.dataset.menuDrawerSubBound !== "1") {
        details.dataset.menuDrawerSubBound = "1";
        details.addEventListener("toggle", () => {
          requestAnimationFrame(() => this.syncSubmenuState(details));
        });
      }

      const summary = details.querySelector(":scope > summary");
      if (summary && summary.dataset.menuDrawerSubSummaryBound !== "1") {
        summary.dataset.menuDrawerSubSummaryBound = "1";
        summary.setAttribute("role", "button");
        summary.addEventListener("click", () => {
          requestAnimationFrame(() => this.syncSubmenuState(details));
        });
      }

      const closeButton = details.querySelector(":scope > .mobile-facets__submenu .mobile-facets__close-button");
      if (closeButton && closeButton.dataset.menuDrawerSubCloseBound !== "1") {
        closeButton.dataset.menuDrawerSubCloseBound = "1";
        closeButton.addEventListener("click", (event) => {
          event.preventDefault();
          details.removeAttribute("open");
          this.syncSubmenuState(details);
          summary && summary.focus();
        });
      }

      this.syncSubmenuState(details);
    });

    this.syncRootState();
  }

  onRootToggle() {
    this.syncRootState();
    if (this.rootDetails && this.rootDetails.hasAttribute("open")) {
      requestAnimationFrame(() => this.bindEvents());
    }
  }

  onDelegatedClick(event) {
    const closeButton = event.target.closest(".mobile-facets__close-button");
    if (closeButton && this.contains(closeButton)) {
      const details = closeButton.closest("details.mobile-facets__details");
      if (details) {
        event.preventDefault();
        details.removeAttribute("open");
        this.syncSubmenuState(details);
        const summary = details.querySelector(":scope > summary");
        if (summary) summary.focus();
        return;
      }
    }

    const summary = event.target.closest("details.mobile-facets__details > summary");
    if (summary && this.contains(summary)) {
      const details = summary.closest("details.mobile-facets__details");
      if (details) {
        requestAnimationFrame(() => this.syncSubmenuState(details));
      }
    }
  }

  syncRootState() {
    if (!this.rootDetails) return;
    const isOpen = this.rootDetails.hasAttribute("open");
    this.rootDetails.classList.toggle("menu-opening", isOpen);
    if (this.rootSummary) {
      this.rootSummary.setAttribute("aria-expanded", isOpen ? "true" : "false");
    }

    document.documentElement.classList.toggle("overflow-hidden-mobile", isOpen);
    document.body.classList.toggle("overflow-hidden-mobile", isOpen);

    if (!isOpen) {
      this.querySelectorAll(".has-submenu.submenu-open").forEach((element) => {
        element.classList.remove("submenu-open");
      });
      this.querySelectorAll("details.mobile-facets__details[open]").forEach((details) => {
        details.removeAttribute("open");
        details.classList.remove("menu-opening");
      });
    }
  }

  syncSubmenuState(details) {
    if (!details) return;
    const isOpen = details.hasAttribute("open");
    details.classList.toggle("menu-opening", isOpen);
    const parentSubmenuContainer = details.closest(".has-submenu");
    if (parentSubmenuContainer) {
      if (isOpen) {
        const siblings = details.parentElement
          ? details.parentElement.querySelectorAll(":scope > details.mobile-facets__details[open]")
          : [];
        siblings.forEach((sibling) => {
          if (sibling !== details) {
            sibling.removeAttribute("open");
            sibling.classList.remove("menu-opening");
          }
        });
      }
      const hasAnyOpen = !!parentSubmenuContainer.querySelector("details.mobile-facets__details[open]");
      parentSubmenuContainer.classList.toggle("submenu-open", hasAnyOpen);
    }
    const summary = details.querySelector(":scope > summary");
    if (summary) {
      summary.setAttribute("aria-expanded", isOpen ? "true" : "false");
    }
  }

  close() {
    if (!this.rootDetails) return;
    this.rootDetails.removeAttribute("open");
    this.syncRootState();
    if (this.rootSummary) this.rootSummary.focus();
  }
}

if (!customElements.get("menu-drawer")) {
  customElements.define("menu-drawer", MenuDrawer);
}

/* ---------------------------
   Pub/Sub
--------------------------- */

/*
  Pub/Sub

  Dawn already provides pub/sub in "assets/pubsub.js" and events in "assets/constants.js".
  Re-declaring the same identifiers here can trigger a global
  "Identifier has already been declared" error and break critical flows (e.g. Add to cart).

  This block ensures window-level fallbacks exist without redeclaring global bindings.
*/
window.PUB_SUB_EVENTS = window.PUB_SUB_EVENTS || (typeof PUB_SUB_EVENTS !== "undefined" ? PUB_SUB_EVENTS : {
  cartUpdate: "cart-update",
  quantityUpdate: "quantity-update",
  optionValueSelectionChange: "option-value-selection-change",
  variantChange: "variant-change",
  cartError: "cart-error"
});

if (typeof subscribe !== "undefined" && typeof subscribe === "function") {
  window.subscribe = window.subscribe || subscribe;
} else if (typeof window.subscribe !== "function") {
  window.__lfdl_subscribers = window.__lfdl_subscribers || {};
  window.subscribe = function (eventName, callback) {
    (window.__lfdl_subscribers[eventName] = window.__lfdl_subscribers[eventName] || []).push(callback);
    return function () {
      window.__lfdl_subscribers[eventName] = (window.__lfdl_subscribers[eventName] || []).filter(function (cb) {
        return cb !== callback;
      });
    };
  };
}

if (typeof publish !== "undefined" && typeof publish === "function") {
  window.publish = window.publish || publish;
} else if (typeof window.publish !== "function") {
  window.publish = function (eventName, data) {
    var subs = (window.__lfdl_subscribers && window.__lfdl_subscribers[eventName]) || [];
    subs.forEach(function (callback) {
      try { callback(data); } catch (e) {}
    });
  };
}

/* ---------------------------
   fetchConfig helper
--------------------------- */

function fetchConfig(type = "json") {
  const config = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: type === "html" ? "text/html" : "application/json",
    },
  };
  return config;
}

window.fetchConfig = fetchConfig;

/* ---------------------------
   Quantity input (plus / moins)
--------------------------- */

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    if (this.input) {
      this.input.addEventListener("change", this.onInputChange.bind(this));
    }
    this.querySelectorAll("button").forEach((btn) => {
      btn.addEventListener("click", this.onButtonClick.bind(this));
    });
  }

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(window.PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) this.quantityUpdateUnsubscriber();
  }

  onInputChange() {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    if (!this.input) return;
    const target = event.currentTarget;
    const action = (target && (target.getAttribute("name") || target.name)) || "";
    const previousValue = this.input.value;
    if (action === "plus") this.input.stepUp();
    else this.input.stepDown();
    if (previousValue !== this.input.value) {
      this.input.dispatchEvent(this.changeEvent);
    }
  }

  validateQtyRules() {
    // Version robuste : on ne bloque rien si les data-* ne sont pas présents.
    if (!this.input) return;
    const min = Number(this.input.getAttribute("min") || "0");
    const max = Number(this.input.getAttribute("max") || "999999");
    let value = Number(this.input.value || "0");
    if (Number.isNaN(value)) value = min;
    value = Math.max(min, Math.min(max, value));
    if (String(value) !== String(this.input.value)) this.input.value = String(value);
  }
}

if (!customElements.get("quantity-input")) {
  customElements.define("quantity-input", QuantityInput);
}

/* ---------------------------
   Variant selects (essentiel)
--------------------------- */

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("change", debounce(this.onVariantChange.bind(this), 30));
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.updateURL();
    this.updateFormID();
    this.dispatchVariantChange();
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll("fieldset"));
    if (fieldsets.length) {
      this.options = fieldsets.map((fieldset) => {
        const checked = fieldset.querySelector("input:checked");
        return checked ? checked.value : "";
      });
      return;
    }

    // fallback: selects
    this.options = Array.from(this.querySelectorAll("select"), (select) => select.value);
  }

  updateMasterId() {
    const variantData = this.getVariantData();
    const match = variantData.find((variant) => {
      return (variant.options || []).every((opt, idx) => opt === this.options[idx]);
    });
    this.currentVariant = match || null;
  }

  updateFormID() {
    const sectionId = this.dataset.section;
    if (!sectionId) return;
    const form = document.getElementById(`product-form-${sectionId}`);
    if (!form) return;
    const idInput = form.querySelector('input[name="id"]');
    if (!idInput) return;
    idInput.value = this.currentVariant ? String(this.currentVariant.id) : "";
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === "false") return;
    const url = new URL(window.location.href);
    url.searchParams.set("variant", String(this.currentVariant.id));
    window.history.replaceState({}, "", url.toString());
  }

  dispatchVariantChange() {
    const sectionId = this.dataset.section || "";
    publish(window.PUB_SUB_EVENTS.variantChange, {
      data: {
        sectionId,
        variant: this.currentVariant,
      },
    });
  }

  getVariantData() {
    if (this.variantData) return this.variantData;
    const json = this.querySelector('[type="application/json"]');
    try {
      this.variantData = json ? JSON.parse(json.textContent) : [];
    } catch {
      this.variantData = [];
    }
    return this.variantData;
  }
}

if (!customElements.get("variant-selects")) {
  customElements.define("variant-selects", VariantSelects);
}

/* ---------------------------
   Product recommendations (lazy load)
--------------------------- */

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);

      const url = this.dataset.url;
      if (!url) return;

      fetch(url)
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement("div");
          html.innerHTML = text;
          const recommendations = html.querySelector("product-recommendations");
          if (recommendations && recommendations.innerHTML.trim().length) {
            this.innerHTML = recommendations.innerHTML;
          } else {
            this.remove();
          }
        })
        .catch((e) => console.error(e));
    };

    new IntersectionObserver(handleIntersection.bind(this), { rootMargin: "0px 0px 400px 0px" }).observe(this);
  }
}

if (!customElements.get("product-recommendations")) {
  customElements.define("product-recommendations", ProductRecommendations);
}
